import { Component } from "@angular/core";
import { FieldWrapper } from "@ngx-formly/core";
import { GridEventService } from "../../../services/grid.service";
import { parseCellValue } from '../../../shared/utils/helpers';

@Component({
    standalone: false,
    templateUrl: "./grid.component.html",
    styleUrls: ["./grid.component.css"]
})
export class FormlyGrid extends FieldWrapper {
    private cellValueMap: Map<string, string | boolean | string[]> = new Map();
    private selectionSet: Set<number> = new Set();

    constructor(private eventService: GridEventService) {
        super();
    }

    ngOnInit() {
        this.model[this.key as string] = this.model[this.key as string] || {};

        this.setSelectionMap();
        this.setDropdownValue();

        const rows: any = this.to.rows || [];

        rows.forEach(row => {
            if (row.columns) {
                (row.columns as any[]).forEach(column => {
                    if (column.type === "Date") {
                        if (column.min) column.min = new Date(column.min);
                        if (column.max) column.max = new Date(column.max);
                        if (column.defaultValue) column.defaultValue = new Date(column.defaultValue);
                    }
                });
            }
        });

        if (Array.isArray((this.model[this.key as string] || {}).cells || [])) {
            ((this.model[this.key as string] || {}).cells || []).forEach(item => {
                const key = `${item.rowIndex}-${item.columnIndex}`;
                const column = this.to?.rows?.[item.rowIndex]?.columns?.[item.columnIndex];
                const value = parseCellValue(item.value);
                if (column?.type === "Checkbox") {
                    let checkboxValue: string[];
                    if (Array.isArray(value)) {
                        checkboxValue = value.map((v: string) => v.trim().replace(/^["']|["']$/g, ""));
                    } else if (typeof value === "string" && value) {
                        checkboxValue = value
                            .split(",")
                            .map((v: string) => v.trim().replace(/^["']|["']$/g, ""));
                    } else {
                        checkboxValue = [];
                    }
                    this.cellValueMap.set(key, checkboxValue);
                } else {
                    this.cellValueMap.set(key, value);
                }
            });
        }
    }

    gridStyles() {
        const to = this.field.templateOptions || {};
        const columnCount = to.headers?.length || 1;
        return {
            'grid-template-columns': `repeat(${columnCount}, 1fr)`,
            gap: to.gap || '10px',
        };
    }

    getRowStyle(rowIndex: number) {
        const to = this.field.templateOptions || {};
        return {
            'grid-row': to.rowSpans?.[rowIndex] ? `span ${to.rowSpans[rowIndex]}` : 'auto',
        };
    }

    getCellStyle(rowIndex: number, colIndex: number) {
        const to = this.field.templateOptions || {};
        return {
            'grid-column': to.colSpans?.[rowIndex]?.[colIndex] ? `span ${to.colSpans[rowIndex][colIndex]}` : 'auto',
        };
    }

    getColumnWidth(columnCount: number): number {
        return 100 / columnCount;
    }

    calculateColumnSum(colIndex: number): number {
        if (!Array.isArray((this.model[this.key as string] || {}).cells || [])) {
            return 0;
        }
        return ((this.model[this.key as string] || {}).cells || []).reduce((sum, cell) => {
            if (cell.columnIndex === colIndex && this.canScore(cell.rowIndex)) {
                const cellValue = cell.value;
                if (cellValue === null || cellValue === undefined || cellValue === '') {
                    return sum;
                }
                return sum + (isNaN(cellValue) ? 0 : parseFloat(cellValue));
            }
            return sum;
        }, 0);
    }

    getModelValue(column: any, rowIndex: number, columnIndex: number) {
        const cellValue = this.cellValueMap.get(`${rowIndex}-${columnIndex}`);
        if (
            (column.type === "Date" || column.type === "Tickbox") &&
            (typeof cellValue == "undefined" || cellValue == null) &&
            column.defaultValue
        ) {
            return column.defaultValue;
        }

        return cellValue;
    }

    setModelValue(column: any, rowIndex: number, colIndex: number, value: any): void {
        if (!(this.model[this.key as string] || {}).cells) {
            (this.model[this.key as string] || {}).cells = [];
        }

        if (column.type === "Date" && value) {
            value = this.convertDate(value);
        }
        else if (column.type === "Tickbox") {
            if (value === true) {
                this.selectionSet.add(rowIndex);
            } else if (value === false) {
                this.selectionSet.delete(rowIndex);
            }
        }

        const cells = (this.model[this.key as string]?.cells ?? []);
        const existingCellIndex = cells.findIndex(
            (cell: any) => cell.rowIndex === rowIndex && cell.columnIndex === colIndex
        );

        if (existingCellIndex !== -1) {
            // Update existing cell
            (this.model[this.key as string] || {}).cells[existingCellIndex].value = parseCellValue(value);
            (this.model[this.key as string] || {}).cells[existingCellIndex].cellId = column.id;
            (this.model[this.key as string] || {}).cells[existingCellIndex].gridCellQuestionId = column.gridCellQuestionId;
        } else {
            // Add new cell
            (this.model[this.key as string] || {}).cells.push({
                cellId: column.id,
                rowIndex: rowIndex,
                columnIndex: colIndex,
                gridCellQuestionId: column.gridCellQuestionId,
                value: parseCellValue(value)
            });
        }

        // Trigger change detection
        this.formControl.setValue((this.model[this.key as string] || {}));
        this.formControl.markAsDirty();
        this.setSelectionMap();
        this.setDropdownValue();
        this.eventService.requestCalculateScores();
    }

    setMinMaxDate(date: string) {
        return date ? new Date(date) : undefined;
    }

    preventDefault(event: Event): void {
        event.preventDefault();
    }

    getDefaultYearRange(): string {
        const currentYear = new Date().getFullYear();
        return `${currentYear - 10}:${currentYear + 10}`;
    }

    private convertDate(date: any): string {
        let value = new Date(date);

        let month =
            value.getMonth() + 1 < 10
                ? "0" + (value.getMonth() + 1)
                : value.getMonth() + 1;

        let day =
            value.getDate() < 10 ? "0" + value.getDate() : value.getDate();

        return value.getFullYear() + "-" + month + "-" + day;
    }

    private setSelectionMap(): void {
        this.selectionSet.clear();
        const rowsRaw = this.field?.templateOptions?.rows;
        const rows: any[] = Array.isArray(rowsRaw) ? rowsRaw : [];
        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
            const row = rows[rowIndex];
            const tickboxColumnIndex = row.columns?.findIndex(
                (column: any) => column?.type === "Tickbox"
            );
            if (tickboxColumnIndex !== undefined && tickboxColumnIndex >= 0) {
                const cell = ((this.model[this.key as string] || {}).cells || [])
                    .find((x: any) => x.rowIndex === rowIndex && x.columnIndex === tickboxColumnIndex);
                if ((cell && parseCellValue(cell.value)) || !cell) {
                    this.selectionSet.add(rowIndex);
                }
            }
        }
    }

    private setDropdownValue(): void {
        const rowsRaw = this.field?.templateOptions?.rows;
        const rows: any[] = Array.isArray(rowsRaw) ? rowsRaw : [];
        const isViewMode = this.field?.templateOptions?.disabled;

        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
            const row = rows[rowIndex];
            const tickboxColumnIndex = row.columns?.findIndex(
                (column: any) => column?.type === "Tickbox"
            );

            // If in view mode, disable all columns in the row
            if (isViewMode) {
                row.columns?.forEach((column: any) => {
                    column.disabled = true;
                });
                continue;
            }
            const dropdownColumnIndex = row.columns?.findIndex(
                (column: any) => column?.type === "Dropdown" || column?.type === "Number" || column?.type === "Textbox" || column?.type === "Textbox"
            );
            row.columns?.forEach((column: any, colIndex: number) => {
                if (colIndex !== tickboxColumnIndex) {
                    if (tickboxColumnIndex !== undefined && tickboxColumnIndex >= 0) {
                        const cell = ((this.model[this.key as string] || {}).cells || [])
                            .find((x: any) => x.rowIndex === rowIndex && x.columnIndex === tickboxColumnIndex);
                        if ((cell && parseCellValue(cell.value)) || !cell) {
                            if (colIndex !== undefined && colIndex >= 0) {
                                if (row.columns[colIndex]) {
                                    row.columns[colIndex].disabled = false;
                                }
                            }
                        } else {
                            if (colIndex !== undefined && colIndex >= 0) {
                                const dropdownCellIndex = ((this.model[this.key as string] || {}).cells || [])
                                    .findIndex((x: any) => x.rowIndex === rowIndex && x.columnIndex === colIndex);
                                if (row.columns[colIndex]) {
                                    if (dropdownCellIndex !== -1) {
                                        (this.model[this.key as string] || {}).cells[dropdownCellIndex].value = '';
                                    }
                                    this.cellValueMap.set(`${rowIndex}-${colIndex}`, '');
                                    row.columns[colIndex].disabled = true;
                                }
                            }
                        }
                    }
                }
            });
            //// Always disable dropdown in view mode
            //if (isViewMode && dropdownColumnIndex !== undefined && dropdownColumnIndex >= 0) {
            //    if (row.columns[dropdownColumnIndex]) {
            //        row.columns[dropdownColumnIndex].disabled = true;
            //    }
            //    continue;
            //}


        }
    }

    private isScoreEnabled(rowIndex: number) {
        return this.selectionSet.has(rowIndex);
    }

    private canScore(rowIndex: number): boolean {
        const hasEnabler = this.selectionSet.size > 0;
        return !hasEnabler || this.isScoreEnabled(rowIndex);
    }
}
